import { useAuth } from '@/hooks/useAuth';
import { Loader2, PlusCircle, Newspaper, Edit, Trash2, PenLine, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  createPeriodicoArticle, getAllPeriodicoArticles, updatePeriodicoArticle,
  deletePeriodicoArticle, getPeriodicoEditors,
} from '@/lib/actions';
import { PeriodicoArticle } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { ArticleImageField } from '@/components/periodico/ArticleImageField';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Image from '@/components/ui/Image';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';

function ArticleForm({ onFormSuccess, articleToEdit, defaultAuthor }: { onFormSuccess: () => void; articleToEdit?: PeriodicoArticle | null; defaultAuthor?: string }) {
  const isEditing = !!articleToEdit;
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);

    const result = isEditing && articleToEdit
      ? await updatePeriodicoArticle(articleToEdit.id, formData)
      : await createPeriodicoArticle(formData);

    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2"><Label htmlFor="title">Titular</Label><Input id="title" name="title" defaultValue={articleToEdit?.title} /></div>
        <div className="space-y-2"><Label htmlFor="subtitle">Subtítulo</Label><Input id="subtitle" name="subtitle" defaultValue={articleToEdit?.subtitle} /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2"><Label htmlFor="authorName">Autor</Label><Input id="authorName" name="authorName" defaultValue={articleToEdit?.authorName ?? defaultAuthor} /></div>
        <div className="space-y-2"><Label htmlFor="category">Categoría</Label><Input id="category" name="category" placeholder="Ej: Sucesos, Cultura..." defaultValue={articleToEdit?.category} /></div>
      </div>
      <ArticleImageField defaultValue={articleToEdit?.imageUrl} />
      <div className="space-y-2">
        <Label htmlFor="columns">Distribución del cuerpo</Label>
        <select
          id="columns"
          name="columns"
          defaultValue={String(articleToEdit?.columns || 1)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="1">1 columna (estándar)</option>
          <option value="2">2 columnas (estilo periódico)</option>
          <option value="3">3 columnas (compacto)</option>
        </select>
        <p className="text-xs text-muted-foreground">Cómo se reparte el texto al leerlo. En móvil siempre se ve a 1 columna.</p>
      </div>
      <div className="space-y-2"><Label htmlFor="content">Contenido del Artículo</Label><RichTextarea id="content" name="content" rows={12} placeholder="Escribe aquí el cuerpo de la noticia. Deja una línea en blanco entre párrafos." defaultValue={articleToEdit?.content} /></div>
      <DialogFooter>
        <DialogClose asChild><Button variant="outline" type="button">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditing ? 'Guardar Cambios' : 'Publicar Artículo'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function PeriodicoRedaccionPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [articles, setArticles] = useState<PeriodicoArticle[]>([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [articleToEdit, setArticleToEdit] = useState<PeriodicoArticle | null>(null);
  const { toast } = useToast();

  const isAdmin = userData?.role === 'admin';

  // Comprobación de acceso: admin o editor autorizado.
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/jartinet', { replace: true }); return; }
    if (isAdmin) { setAuthorized(true); return; }
    getPeriodicoEditors().then((editors) => {
      const ok = editors.includes(user.uid);
      setAuthorized(ok);
      if (!ok) navigate('/jartinet', { replace: true });
    });
  }, [user, userData, loading, isAdmin, navigate]);

  const fetchArticles = async () => {
    setIsLoadingArticles(true);
    const { articles } = await getAllPeriodicoArticles();
    setArticles(articles);
    setIsLoadingArticles(false);
  };

  useEffect(() => {
    if (authorized) fetchArticles();
  }, [authorized]);

  const handleFormSuccess = () => { setIsDialogOpen(false); setArticleToEdit(null); fetchArticles(); };
  const handleEditClick = (article: PeriodicoArticle) => { setArticleToEdit(article); setIsDialogOpen(true); };
  const handleNewClick = () => { setArticleToEdit(null); setIsDialogOpen(true); };

  const handleDelete = async (articleId: string) => {
    const result = await deletePeriodicoArticle(articleId);
    if (result.success) { toast({ title: 'Éxito', description: result.message }); fetchArticles(); }
    else toast({ title: 'Error', description: result.error, variant: 'destructive' });
  };

  if (loading || authorized === null) {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando acceso...</p>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center text-center">
        <Lock className="h-12 w-12 text-muted-foreground mb-3" />
        <h2 className="text-xl font-bold">Sin acceso</h2>
        <p className="text-muted-foreground">No tienes permiso para acceder a la redacción del periódico.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-blue-500/15 via-card/40 to-card/20 p-8"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-blue-500/40 blur-xl" />
              <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-blue-400/30 bg-blue-500/15 text-blue-300"><PenLine className="h-9 w-9" /></div>
            </div>
            <div>
              <h1 className="font-headline text-4xl font-bold">Redacción del Periódico</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">Redacta y edita los artículos de "El Diario de Jartiland".</p>
            </div>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) setArticleToEdit(null); setIsDialogOpen(open); }}>
            <DialogTrigger asChild>
              <Button size="lg" onClick={handleNewClick}><PlusCircle className="mr-2 h-5 w-5" /> Nuevo Artículo</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>{articleToEdit ? 'Editar Artículo' : 'Crear Nuevo Artículo'}</DialogTitle>
                <DialogDescription>Rellena todos los campos para publicar en el periódico.</DialogDescription>
              </DialogHeader>
              <ArticleForm onFormSuccess={handleFormSuccess} articleToEdit={articleToEdit} defaultAuthor={userData?.nickname} />
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold font-headline">Artículos Publicados</h2>
        {isLoadingArticles ? (
          <div className="flex justify-center items-center h-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map((article) => (
              <Card key={article.id} className="flex flex-col">
                <div className="relative w-full aspect-video rounded-t-lg overflow-hidden">
                  <Image src={article.imageUrl} alt={article.title} className="object-cover w-full h-full" />
                </div>
                <div className="p-4 flex flex-col flex-grow">
                  <h3 className="font-bold text-lg leading-tight">{article.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">Por: {article.authorName}</p>
                  <p className="text-xs text-muted-foreground mt-auto pt-2">{format(new Date(article.createdAt), 'dd/MM/yyyy', { locale: es })}</p>
                </div>
                <div className="p-2 border-t flex justify-end gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleEditClick(article)}><Edit className="h-4 w-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar este artículo?</AlertDialogTitle>
                        <AlertDialogDescription>Esta acción es permanente y no se puede deshacer.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(article.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <Newspaper className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No hay artículos</h3>
            <p className="mt-1 text-sm text-muted-foreground">Crea tu primer artículo para empezar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
